import { SxProps, Theme } from '@mui/material';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import React, { type JSX } from 'react';

import { CopyToClipboard } from '~components/CopyToClipboard/CopyToClipboard';
import HighlightedText from '~components/HighlightedText/HighlightedText';
import { useOrderSearchHighlightTerm } from '~components/OrderSearchHighlightContext/OrderSearchHighlightContext';
import { Order } from '~hooks/useOrders/models';
import { makeOrderSenderDisplay } from '~utils/orders';

import OrderRateCardIndicator from './OrderRateCardIndicator';
import ServiceClassChip from './ServiceClassChip';
import useOrderCustomerRate from './useOrderCustomerRate';

interface OrderCardSubtitleProps {
  hideMaterialsSettingEnabled?: boolean;
  order: Order;
  sx?: SxProps<Theme>;
}

export default function OrderCardSubtitle({
  hideMaterialsSettingEnabled,
  order,
  sx,
}: OrderCardSubtitleProps) {
  const fragments: Array<JSX.Element> = [];
  const orderSearchHighlightTerm = useOrderSearchHighlightTerm();
  const highlightIndexedText = (text: string) => (
    <HighlightedText searchTerm={orderSearchHighlightTerm} text={text} />
  );

  const { customerRateText, shouldShowRateCardIndicator } = useOrderCustomerRate(order);

  const senderDisplay = makeOrderSenderDisplay(order);
  const shouldShowSender = Boolean(senderDisplay);

  if (shouldShowSender) {
    fragments.push(
      <Typography
        component="span"
        sx={(theme) => ({
          color: theme.brandV2.colors.treadGray1,
          fontSize: 'inherit',
          fontWeight: 600,
        })}
      >
        {t('common.customer')}:{' '}
        <Typography
          component="span"
          sx={(theme) => ({
            color: theme.brandV2.colors.treadGreen,
            fontSize: 'inherit',
            fontWeight: 600,
          })}
        >
          {highlightIndexedText(senderDisplay)}
        </Typography>
      </Typography>,
    );
  }

  if (order.account?.name) {
    fragments.push(
      <Typography
        component="span"
        sx={(theme) => ({
          color: theme.brandV2.colors.treadGray1,
          fontSize: 'inherit',
          fontWeight: 600,
        })}
      >
        {t(shouldShowSender ? 'common.project_account' : 'common.customer')}:{' '}
        <Typography
          component="span"
          sx={(theme) => ({
            color: theme.brandV2.colors.treadGreen,
            fontSize: 'inherit',
            fontWeight: 600,
          })}
        >
          {highlightIndexedText(order.account.name)}
        </Typography>
      </Typography>,
    );
  }

  if (order.chargeToAccount) {
    fragments.push(
      <Typography
        component="span"
        sx={(theme) => ({
          color: theme.brandV2.colors.treadGray1,
          fontSize: 'inherit',
          fontWeight: 600,
        })}
      >
        {t('dispatch.order.charge_to_account')}:{' '}
        <Typography
          component="span"
          sx={(theme) => ({
            color: theme.brandV2.colors.treadGreen,
            fontSize: 'inherit',
            fontWeight: 600,
          })}
        >
          {order.chargeToAccount}
        </Typography>
      </Typography>,
    );
  }
  if (order.poJobNumber) {
    fragments.push(
      <Typography
        component="span"
        sx={(theme) => ({
          color: theme.brandV2.colors.treadGray1,
          fontSize: 'inherit',
          fontWeight: 600,
        })}
      >
        {t('order.form.order_po')}:{' '}
        <Typography
          component="span"
          sx={(theme) => ({
            color: theme.brandV2.colors.treadBlack,
            fontSize: 'inherit',
            fontWeight: 400,
          })}
        >
          {order.poJobNumber}
        </Typography>
      </Typography>,
    );
  }

  if (order.zone) {
    fragments.push(
      <Typography
        component="span"
        sx={(theme) => ({
          color: theme.brandV2.colors.treadGray1,
          fontSize: 'inherit',
          fontWeight: 600,
        })}
      >
        {t('order.form.order_zone')}:{' '}
        <Typography
          component="span"
          sx={(theme) => ({
            color: theme.brandV2.colors.treadBlack,
            fontSize: 'inherit',
            fontWeight: 400,
          })}
        >
          {order.zone}
        </Typography>
      </Typography>,
    );
  }

  if (order.externalId) {
    fragments.push(
      <Typography
        component="span"
        sx={(theme) => ({
          color: theme.brandV2.colors.treadGray1,
          fontSize: 'inherit',
          fontWeight: 600,
        })}
      >
        {t('form_fields.external_id')}:{' '}
        <Typography
          component="span"
          sx={(theme) => ({
            color: theme.brandV2.colors.treadBlack,
            fontSize: 'inherit',
            fontWeight: 400,
          })}
        >
          {highlightIndexedText(order.externalId)}
        </Typography>
      </Typography>,
    );
  }

  if (order.serviceClass?.name) {
    fragments.push(
      <Typography
        component="span"
        sx={(theme) => ({
          color: theme.brandV2.colors.treadGray1,
          fontSize: 'inherit',
          fontWeight: 600,
        })}
      >
        {t('form_fields.service_class')}:{' '}
        <ServiceClassChip serviceClass={order.serviceClass} />
      </Typography>,
    );
  }

  if (customerRateText) {
    fragments.push(
      <Typography
        component="span"
        sx={(theme) => ({
          color: theme.brandV2.colors.treadGray1,
          fontSize: 'inherit',
          fontWeight: 600,
        })}
      >
        {t('dispatch.job.customer_rate')}:{' '}
        <Typography
          component="span"
          sx={(theme) => ({
            color: theme.brandV2.colors.treadBlack,
            fontSize: 'inherit',
            fontWeight: 400,
          })}
        >
          {customerRateText}
        </Typography>
        {shouldShowRateCardIndicator && <OrderRateCardIndicator order={order} />}
      </Typography>,
    );
  }

  // The rate cards that apply to an order are independent of whether it has a customer rate
  // to display, so the indicator still needs a home when that fragment is absent.
  if (shouldShowRateCardIndicator && !customerRateText) {
    fragments.push(<OrderRateCardIndicator order={order} />);
  }

  if (order.material?.name && !hideMaterialsSettingEnabled) {
    fragments.push(
      <Typography
        component="span"
        sx={(theme) => ({
          color: theme.brandV2.colors.treadGray1,
          fontSize: 'inherit',
          fontWeight: 600,
        })}
      >
        {t('form_fields.material')}:{' '}
        <Typography
          component="span"
          sx={(theme) => ({
            color: theme.brandV2.colors.treadBlack,
            fontSize: 'inherit',
            fontWeight: 400,
          })}
        >
          {order.material.name}
        </Typography>
      </Typography>,
    );
  }

  if (order.projectName) {
    fragments.push(
      <Typography
        component="span"
        sx={(theme) => ({
          color: theme.brandV2.colors.treadGray1,
          fontSize: 'inherit',
          fontWeight: 600,
        })}
      >
        {t('common.project')}:{' '}
        <Typography
          component="span"
          sx={(theme) => ({
            color: theme.brandV2.colors.treadBlack,
            fontSize: 'inherit',
            fontWeight: 400,
          })}
        >
          {highlightIndexedText(order.projectName)}
        </Typography>
      </Typography>,
    );
  }

  if (order.phase?.code) {
    fragments.push(
      <Typography
        component="span"
        sx={(theme) => ({
          color: theme.brandV2.colors.treadGray1,
          fontSize: 'inherit',
          fontWeight: 600,
        })}
      >
        {t('form_fields.phase_code')}:{' '}
        <Typography
          component="span"
          sx={(theme) => ({
            color: theme.brandV2.colors.treadBlack,
            fontSize: 'inherit',
            fontWeight: 400,
          })}
        >
          {order.phase.code}
        </Typography>
      </Typography>,
    );
  }

  if (order.project?.externalId) {
    fragments.push(
      <Typography
        component="span"
        sx={(theme) => ({
          color: theme.brandV2.colors.treadGray1,
          fontSize: 'inherit',
          fontWeight: 600,
        })}
      >
        {t('dispatch.dispatch_v3.project_external_id')}:{' '}
        <Typography
          component="span"
          sx={(theme) => ({
            color: theme.brandV2.colors.treadBlack,
            fontSize: 'inherit',
            fontWeight: 400,
          })}
        >
          {highlightIndexedText(order.project.externalId)}
        </Typography>
      </Typography>,
    );
  }

  fragments.push(
    <CopyToClipboard
      textToCopy={order.orderId}
      hideTextWhileCopying
      alertMessageDuration={1000}
    >
      <Typography
        component="span"
        sx={(theme) => ({
          color: theme.brandV2.colors.treadBlack,
          fontSize: 'inherit',
          fontWeight: 400,
        })}
      >
        {highlightIndexedText(order.orderId)}
      </Typography>
    </CopyToClipboard>,
  );

  return (
    <Typography
      data-test-id={`order-card-subtitle-${order.orderId}`}
      sx={[() => ({ fontSize: '14px' }), ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {fragments.map((fragment, index) => (
        <React.Fragment key={index}>
          {fragment}

          {index !== fragments.length - 1 && (
            <Typography
              component="span"
              sx={{ color: 'text.primary', fontSize: 'inherit', mx: 0.5 }}
            >
              {'•'}
            </Typography>
          )}
        </React.Fragment>
      ))}
    </Typography>
  );
}
